import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useRangeQuery } from "@/lib/useData";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  PageTitle, Card, SectionTitle, StatusChip, PartialChip, MetricTip, Takeaway,
  LoadingBlocks, ErrorState, Forbidden, GroupTooSmall,
} from "@/components/shared/primitives";
import { BarRanking, LineTrend } from "@/components/shared/charts";
import { SegmentedControl, DataTable } from "@/components/kit";
import { pct, money, num, shortDate, relativeLastActive, METRIC_DEFS } from "@/lib/format";

function Stat({ label, def, value, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[13px] mb-1">{def ? <MetricTip label={label} def={def} /> : <span className="text-nb-ink2">{label}</span>}</div>
      <div className="text-[22px] font-semibold text-nb-ink tnum">{value}</div>
    </div>
  );
}

export default function AppDetail() {
  const { id } = useParams();
  const { role } = useAuth();
  const [rankBy, setRankBy] = useState("uses");
  const { data, isLoading, error, refetch } = useRangeQuery(`app-${id}`, `/apps/${id}`);

  if (error?.code === "forbidden") return <Forbidden who="Admins and IT Admins" />;
  if (isLoading) return <LoadingBlocks rows={5} />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;

  const d = data.data;
  const s = d.summary;
  const featureData = d.features.map((f) => ({ name: f.name, value: rankBy === "uses" ? f.uses : f.distinct_users }));
  const topFeature = d.features[0];

  const removeSeat = async (memberId, name) => {
    try {
      await api.post(`/apps/${id}/seats/${memberId}/remove`);
      toast.success(`Seat removed from ${name}.`);
      refetch();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div>
      <Link to="/apps" className="inline-flex items-center gap-1.5 text-[13px] text-nb-ink2 hover:text-nb-ink mb-3 transition-colors">
        <ArrowLeft size={14} /> All apps
      </Link>
      <PageTitle sub={`${s.vendor} · how is this tool used, and by whom?`} testid="app-detail-page"
        actions={<StatusChip status={s.status} testid="app-status" />}>
        {s.name}
      </PageTitle>

      <Card className="p-5 mb-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <Stat testid="stat-seats" label="Seats vs active" value={`${s.active_users} / ${s.seats_bought}`} />
          <Stat testid="stat-cpau" label="Cost per active user" def={METRIC_DEFS.cost_per_active_user}
            value={s.cost_per_active_user === null ? "No active users" : money(s.cost_per_active_user)} />
          <Stat testid="stat-renewal" label="Renewal date" value={shortDate(s.renewal_date)} />
          <Stat testid="stat-depth" label="Depth" def={METRIC_DEFS.depth} value={s.depth} />
        </div>
        {(s.partial_coverage || s.too_early) && (
          <div className="mt-4 flex items-center gap-3">
            {s.partial_coverage && <PartialChip />}
            {s.partial_coverage && <span className="text-[13px] text-nb-ink2">Data from a vendor report, no feature-level detail.</span>}
            {s.too_early && <span className="text-[13px] text-nb-ink2">Added recently, trends are hidden until there are four weeks of data.</span>}
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card className="p-5">
          <div className="flex items-center justify-between mb-1">
            <SectionTitle>Feature usage</SectionTitle>
            <SegmentedControl value={rankBy} onChange={setRankBy} testid="feature-rank"
              options={[{ value: "uses", label: "By uses" }, { value: "users", label: "By users" }]} />
          </div>
          {featureData.length === 0 ? (
            <p className="text-[14px] text-nb-ink2 mt-4">No feature-level usage in this period yet.</p>
          ) : (
            <>
              <Takeaway>{topFeature && `${topFeature.name} is the most-used feature this period.`}</Takeaway>
              <BarRanking data={featureData} testid="feature-bars" />
            </>
          )}
          {d.unused_features.length > 0 && (
            <div className="mt-5 pt-4 border-t border-nb-border" data-testid="unused-features">
              <p className="text-[13px] text-nb-ink2 mb-2">Never used this period, training opportunities</p>
              <div className="flex flex-wrap gap-2">
                {d.unused_features.map((f) => (
                  <span key={f.id} className="text-[13px] text-nb-ink2 border border-nb-border rounded-full px-3 py-1 bg-nb-border/20">{f.name}</span>
                ))}
              </div>
            </div>
          )}
        </Card>

        <Card className="p-5">
          <SectionTitle className="mb-1">Usage over time</SectionTitle>
          {s.too_early ? (
            <p className="text-[14px] text-nb-ink2 mt-4">Too early to tell, trends appear after four weeks.</p>
          ) : (
            <>
              <Takeaway>Weekly active users and active minutes, last 12 weeks.</Takeaway>
              <LineTrend
                data={d.trend}
                series={[
                  { key: "active_users", name: "Active users", color: "#788CE3" },
                  { key: "active_minutes", name: "Active minutes", color: "#17243F", axis: "right" },
                ]}
                testid="usage-trend"
              />
            </>
          )}
        </Card>
      </div>

      <Card className="p-5 mb-8" data-testid="by-team">
        <SectionTitle className="mb-1">By team</SectionTitle>
        <Takeaway>Which practice groups have picked this tool up, and how many of their seats are active.</Takeaway>
        <div className="overflow-x-auto">
          <table className="w-full text-[14px]">
            <thead>
              <tr className="text-[13px] text-nb-ink2 text-left border-b border-nb-border">
                <th className="py-2 font-medium">Team</th>
                <th className="py-2 font-medium text-right">Active users</th>
                <th className="py-2 font-medium text-right">Seats (used/held)</th>
                <th className="py-2 font-medium text-right">Utilisation</th>
                <th className="py-2 font-medium">Top feature</th>
              </tr>
            </thead>
            <tbody>
              {d.by_team.map((t) => (
                <tr key={t.team_id} className="border-b border-nb-border last:border-0 hover:bg-nb-sand/10 transition-colors">
                  <td className="py-3"><Link to={`/teams/${t.team_id}`} className="text-nb-ink hover:text-nb-sage">{t.team}</Link></td>
                  {t.group_too_small ? (
                    <td colSpan={4} className="py-3"><GroupTooSmall /></td>
                  ) : (
                    <>
                      <td className="py-3 tnum text-right">{t.active_users}</td>
                      <td className="py-3 tnum text-right">{t.active_users} / {t.seats_assigned}</td>
                      <td className="py-3 tnum text-right">{pct(t.utilisation)}</td>
                      <td className="py-3">{t.top_feature || "-"}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="p-5" data-testid="seat-holders">
        <SectionTitle className="mb-1">Members</SectionTitle>
        {d.members_hidden ? (
          <p className="text-[14px] text-nb-ink2 mt-3">Member-level data is limited to Admins. Ask your AI Lead to enable it in Settings.</p>
        ) : (
          <>
            <Takeaway>Everyone holding a seat for this tool. Sort by any column to find who to follow up with.</Takeaway>
            <div className="mt-2" data-testid="seat-holders-table">
              <DataTable
                getRowKey={(m) => m.member_id}
                rows={d.members}
                defaultSort={{ key: "name", dir: "asc" }}
                columns={[
                  { key: "name", label: "Member", always: true,
                    render: (m) => (
                      <span>
                        <Link to={`/members/${m.member_id}`} className="hover:text-[var(--text-link)]" onClick={(e) => e.stopPropagation()}>{m.name}</Link>
                        {!m.is_active && <span className="ml-2 text-[12px] text-nb-amber font-normal">Never active</span>}
                      </span>
                    ) },
                  { key: "team", label: "Team", render: (m) => m.team || "-" },
                  { key: "location", label: "Office", render: (m) => m.location || "-" },
                  { key: "last_active", label: "Last active", sortValue: (m) => m.last_active || "",
                    render: (m) => <span className="font-normal text-nb-ink2">{relativeLastActive(m.last_active)}</span> },
                  ...(role === "admin" ? [{ key: "action", label: "", sortable: false,
                    render: (m) => (
                      <button data-testid={`remove-seat-${m.member_id}`} onClick={(e) => { e.stopPropagation(); removeSeat(m.member_id, m.name); }}
                        className="text-[12.5px] text-nb-ink2 hover:text-nb-ink border border-nb-border rounded-full px-3 py-1 transition-colors">
                        Remove seat
                      </button>
                    ) }] : []),
                ]}
              />
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
